import { Button, Dialog, DialogTitle } from "@mui/material";
import { alpha, Box, Grid } from "@mui/system";
import React from "react";
import {
    DialogContent,
    DialogActions
} from "@mui/material";
import {
    Typography,
    Chip,
    List,
    ListItem,
    Avatar,
    Divider,
} from "@mui/material";
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import CalendarIcon from '@mui/icons-material/CalendarToday';


interface Props {
    tenantsHistoryDialogOpen: boolean;
    setTenantsHistoryDialogOpen: (open: boolean) => void;
    apartmentById: any;
    setAddTenantDialogOpen: (open: boolean) => void;
}

export const TenantHistoryDialog: React.FC<Props> = ({
    tenantsHistoryDialogOpen,
    setTenantsHistoryDialogOpen,
    apartmentById,
    setAddTenantDialogOpen
}) => {
    return (
        <Dialog
            open={tenantsHistoryDialogOpen}
            onClose={() => setTenantsHistoryDialogOpen(false)}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon sx={{ color: '#4DB6AC' }} />
                        <Typography variant="h6" fontWeight="bold">
                            Historial de Inquilinos
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                            label={`${apartmentById?.history?.length || 0} inquilinos`}
                            size="small"
                            sx={{
                                backgroundColor: alpha('#4DB6AC', 0.1),
                                color: '#4DB6AC',
                                fontWeight: 'bold'
                            }}
                        />
                    </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Apartamento {apartmentById?.apartmentNumber} - {apartmentById?.complex.name}
                </Typography>
            </DialogTitle>

            <DialogContent>
                {/* Estadísticas rápidas */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid size={4}>
                        <Box
                            sx={{
                                backgroundColor: alpha('#4DB6AC', 0.1),
                                borderRadius: 2,
                                p: 2,
                                textAlign: 'center'
                            }}
                        >
                            <Typography variant="caption" color="text.secondary">
                                Total Inquilinos
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" color="#4DB6AC">
                                {apartmentById?.history?.length || 0}
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid size={4}>
                        <Box
                            sx={{
                                backgroundColor: alpha('#4CAF50', 0.1),
                                borderRadius: 2,
                                p: 2,
                                textAlign: 'center'
                            }}
                        >
                            <Typography variant="caption" color="text.secondary">
                                Inquilino Actual
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" color="#4CAF50">
                                {apartmentById?.history?.filter((h: any) => h.isCurrentTenant).length || 0}
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid size={4}>
                        <Box
                            sx={{
                                backgroundColor: alpha('#FF9800', 0.1),
                                borderRadius: 2,
                                p: 2,
                                textAlign: 'center'
                            }}
                        >
                            <Typography variant="caption" color="text.secondary">
                                Tiempo Promedio
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" color="#FF9800">
                                {(() => {
                                    const histories = apartmentById?.history?.filter((h: any) => h.moveOutDate);
                                    if (!histories || histories.length === 0) return 'N/A';

                                    const avgDays = histories.reduce((sum: number, h: any) => {
                                        const days = Math.floor(
                                            (new Date(h.moveOutDate!).getTime() - new Date(h.moveInDate).getTime())
                                            / (1000 * 60 * 60 * 24)
                                        );
                                        return sum + days;
                                    }, 0) / histories.length;

                                    const months = Math.floor(avgDays / 30);
                                    return `${months} m`;
                                })()}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>

                <Divider sx={{ mb: 2 }} />

                {/* Timeline de Inquilinos */}
                {apartmentById?.history && apartmentById.history.length > 0 ? (
                    <List sx={{ maxHeight: 450, overflow: 'auto' }}>
                        {apartmentById.history
                            .sort((a: any, b: any) => new Date(b.moveInDate).getTime() - new Date(a.moveInDate).getTime())
                            .map((historyItem: any, index: number) => {
                                const calculateStayDuration = () => {
                                    const start = new Date(historyItem.moveInDate);
                                    const end = historyItem.moveOutDate ? new Date(historyItem.moveOutDate) : new Date();
                                    const days = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
                                    const months = Math.floor(days / 30);
                                    const remainingDays = days % 30;

                                    if (months === 0) return `${days} días`;
                                    if (remainingDays === 0) return `${months} ${months === 1 ? 'mes' : 'meses'}`;
                                    return `${months} ${months === 1 ? 'mes' : 'meses'} y ${remainingDays} días`;
                                };

                                return (
                                    <React.Fragment key={historyItem.id}>
                                        <ListItem
                                            sx={{
                                                flexDirection: 'column',
                                                alignItems: 'stretch',
                                                backgroundColor: historyItem.isCurrentTenant
                                                    ? alpha('#4CAF50', 0.05)
                                                    : index % 2 === 0
                                                        ? alpha('#4DB6AC', 0.02)
                                                        : 'transparent',
                                                borderRadius: 2,
                                                mb: 2,
                                                p: 2.5,
                                                border: historyItem.isCurrentTenant ? `2px solid ${alpha('#4CAF50', 0.3)}` : 'none',
                                                position: 'relative'
                                            }}
                                        >
                                            {/* Badge de inquilino actual */}
                                            {historyItem.isCurrentTenant && (
                                                <Chip
                                                    label="ACTUAL"
                                                    size="small"
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 10,
                                                        right: 10,
                                                        backgroundColor: '#4CAF50',
                                                        color: 'white',
                                                        fontWeight: 'bold',
                                                        height: 24
                                                    }}
                                                />
                                            )}

                                            {/* Header con avatar e info del inquilino */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                                <Avatar
                                                    sx={{
                                                        width: 56,
                                                        height: 56,
                                                        backgroundColor: historyItem.isCurrentTenant ? '#4CAF50' : '#4DB6AC',
                                                        fontSize: '1.5rem'
                                                    }}
                                                >
                                                    {historyItem.tenant.fullName.charAt(0)}
                                                </Avatar>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="h6" fontWeight="bold">
                                                        {historyItem.tenant.fullName}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                                        <PhoneIcon sx={{ fontSize: 16, color: '#4DB6AC' }} />
                                                        <Typography variant="body2" color="text.secondary">
                                                            {historyItem.tenant.primaryPhone}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>

                                            {/* Fechas de estancia */}
                                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                                <Grid size={6}>
                                                    <Box
                                                        sx={{
                                                            backgroundColor: alpha('#4CAF50', 0.1),
                                                            borderRadius: 1.5,
                                                            p: 1.5
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                            <CalendarIcon sx={{ fontSize: 16, color: '#4CAF50' }} />
                                                            <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                                                Fecha de Entrada
                                                            </Typography>
                                                        </Box>
                                                        <Typography variant="body2" fontWeight="bold">
                                                            {new Date(historyItem.moveInDate).toLocaleDateString('es-CR', {
                                                                year: 'numeric',
                                                                month: 'long',
                                                                day: 'numeric'
                                                            })}
                                                        </Typography>
                                                    </Box>
                                                </Grid>

                                                <Grid size={6}>
                                                    <Box
                                                        sx={{
                                                            backgroundColor: historyItem.moveOutDate
                                                                ? alpha('#F44336', 0.1)
                                                                : alpha('#FF9800', 0.1),
                                                            borderRadius: 1.5,
                                                            p: 1.5
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                            <CalendarIcon sx={{
                                                                fontSize: 16,
                                                                color: historyItem.moveOutDate ? '#F44336' : '#FF9800'
                                                            }} />
                                                            <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                                                Fecha de Salida
                                                            </Typography>
                                                        </Box>
                                                        <Typography variant="body2" fontWeight="bold">
                                                            {historyItem.moveOutDate
                                                                ? new Date(historyItem.moveOutDate).toLocaleDateString('es-CR', {
                                                                    year: 'numeric',
                                                                    month: 'long',
                                                                    day: 'numeric'
                                                                })
                                                                : 'Actualmente residiendo'
                                                            }
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            </Grid>

                                            {/* Duración de estancia */}
                                            <Box
                                                sx={{
                                                    backgroundColor: alpha('#4DB6AC', 0.1),
                                                    borderRadius: 1.5,
                                                    p: 1.5,
                                                    mb: 2,
                                                    textAlign: 'center'
                                                }}
                                            >
                                                <Typography variant="caption" color="text.secondary">
                                                    Duración de Estancia
                                                </Typography>
                                                <Typography variant="body1" fontWeight="bold" color="#4DB6AC">
                                                    {calculateStayDuration()}
                                                </Typography>
                                            </Box>

                                            {/* Información adicional */}
                                            {(historyItem.reasonForLeaving || historyItem.securityDepositReturned !== undefined || historyItem.notes) && (
                                                <>
                                                    <Divider sx={{ my: 2 }} />

                                                    {historyItem.reasonForLeaving && (
                                                        <Box sx={{ mb: 2 }}>
                                                            <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                                                Motivo de Salida:
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                                {historyItem.reasonForLeaving}
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                    {historyItem.securityDepositReturned !== undefined && (
                                                        <Box sx={{ mb: 2 }}>
                                                            <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                                                Depósito Devuelto:
                                                            </Typography>
                                                            <Typography
                                                                variant="body2"
                                                                fontWeight="bold"
                                                                color="#4CAF50"
                                                                sx={{ mt: 0.5 }}
                                                            >
                                                                {apartmentById?.currency === 'USD' ? '$' : '₡'}
                                                                {historyItem.securityDepositReturned && historyItem.securityDepositReturned.toLocaleString()}
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                    {historyItem.notes && (
                                                        <Box
                                                            sx={{
                                                                backgroundColor: alpha('#FF9800', 0.1),
                                                                borderRadius: 1,
                                                                p: 1.5
                                                            }}
                                                        >
                                                            <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                                                Notas:
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                                {historyItem.notes}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </>
                                            )}

                                            {/* Fecha de registro */}
                                            <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${alpha('#4DB6AC', 0.1)}` }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Registrado el {new Date(historyItem.createdAt).toLocaleDateString('es-CR')}
                                                </Typography>
                                            </Box>
                                        </ListItem>
                                    </React.Fragment>
                                );
                            })}
                    </List>
                ) : (
                    <Box
                        sx={{
                            textAlign: 'center',
                            py: 6,
                            px: 3
                        }}
                    >
                        <PersonIcon sx={{ fontSize: 60, color: '#E0E0E0', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            No hay historial de inquilinos
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Este apartamento no tiene inquilinos registrados todavía
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<PersonIcon />}
                            onClick={() => {
                                setTenantsHistoryDialogOpen(false);
                                setAddTenantDialogOpen(true);
                            }}
                            sx={{
                                backgroundColor: '#4DB6AC',
                                '&:hover': {
                                    backgroundColor: '#26A69A'
                                }
                            }}
                        >
                            Asignar Primer Inquilino
                        </Button>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3 }}>
                <Button onClick={() => setTenantsHistoryDialogOpen(false)}>
                    Cerrar
                </Button>
            </DialogActions>
        </Dialog>
    )
}